import { GpsFixed } from "@mui/icons-material";
import { Box, IconButton, SvgIcon, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { decomposeRotation } from "@novorender/api";
import { memo, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { HudPanel } from "components/hudPanel";
import IconButtonExt from "components/iconButtonExt";
import { useCameraState } from "contexts/cameraState";
import { useLastPickSample } from "contexts/lastPickSample";
import { LocationStatus, selectLocationStatus } from "features/myLocation/myLocationSlice";
import { useGoToMyLocation } from "features/myLocation/useGoToMyLocation";
import { selectIsTopDown } from "features/orthoCam";
import { CameraType, renderActions, selectCamera, selectNavigationCube } from "features/render";
import { tm2LatLon } from "features/render/utils";
import { useUom } from "features/uom/hooks/useUom";
import { UnitType } from "features/uom/spec";
import { convertAndFormat } from "features/uom/utils";
import { explorerActions, selectTmZoneForCalc } from "slices/explorer";
import { telemetry } from "telemetry";

export default memo(function LocationHudInner() {
    const theme = useTheme();
    const small = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Box
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 1050,
            }}
        >
            <Box display="flex" gap={small ? theme.customSpacing.hudPaddingMobile : theme.customSpacing.hudPadding}>
                <CompassBtn />
                <MyLocationBtn />
                <CursorCoordinates small={small} />
                {!small && <Scale small={false} />}
            </Box>
        </Box>
    );
});

function CompassBtn() {
    const cameraState = useCameraState();

    const theme = useTheme();
    const { t } = useTranslation();
    const navigationCube = useAppSelector(selectNavigationCube);
    const dispatch = useAppDispatch();
    const { showCompass } = navigationCube;

    const rotation = useMemo(() => {
        if (!cameraState || !showCompass) {
            return 0;
        }

        const { yaw } = decomposeRotation(cameraState.rotation);
        return yaw;
    }, [cameraState, showCompass]);

    return (
        <HudPanel sx={{ pointerEvents: "auto" }}>
            <Tooltip title={t("navigationCube")} placement="bottom">
                <Box>
                    <IconButton
                        onClick={() => {
                            dispatch(renderActions.setNavigationCube({ enabled: !navigationCube.enabled }));
                        }}
                        sx={navigationCube.enabled ? { outline: `3px solid ${theme.palette.primary.main}` } : {}}
                    >
                        <SvgIcon>
                            <svg
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ rotate: `${rotation}rad`, opacity: showCompass ? 1 : 0.5 }}
                            >
                                <path
                                    d="M 12 1 l -6 10 l 1 1 l 5 -2 l 5 2 l 1 -1 z"
                                    fill={theme.palette.primary.main}
                                />
                                <path d="M 12 23 l 6 -10 l -1 -1 l -5 2 l -5 -2 l -1 1 z" />
                            </svg>
                        </SvgIcon>
                    </IconButton>
                </Box>
            </Tooltip>
        </HudPanel>
    );
}

function MyLocationBtn() {
    const goToMyLocation = useGoToMyLocation();
    const status = useAppSelector(selectLocationStatus);
    const loading = status.status === LocationStatus.Loading;
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        if (status.status === LocationStatus.Error) {
            dispatch(explorerActions.setSnackbarMessage({ msg: status.msg }));
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                dispatch(explorerActions.setSnackbarMessage(null));
                timeoutRef.current = undefined;
            }, 3000);
        }
    }, [dispatch, status]);

    return (
        <HudPanel sx={{ pointerEvents: "auto" }}>
            <Tooltip title={t("myLocation")} placement="bottom">
                <Box>
                    <IconButtonExt onClick={goToMyLocation} disabled={loading} loading={loading}>
                        <GpsFixed />
                    </IconButtonExt>
                </Box>
            </Tooltip>
        </HudPanel>
    );
}

function CursorCoordinates({ small }: { small: boolean }) {
    const theme = useTheme();
    const position = useLastPickSample()?.position;
    const tmZone = useAppSelector(selectTmZoneForCalc);
    const [showXyz, setShowXyz] = useState(true);
    const { t } = useTranslation();
    const uom = useUom();

    let content: ReactNode;
    const showLatLon = tmZone && !showXyz;

    if (showLatLon) {
        let lon = "-",
            lat = "-",
            alt = "-";

        try {
            if (position) {
                const coords = tm2LatLon({ coords: position, tmZone });
                lon = coords.longitude.toFixed(5);
                lat = coords.latitude.toFixed(5);
                alt = convertAndFormat(uom, position[2], UnitType.length, { decimals: small ? 0 : 3 });
            }

            content = (
                <>
                    <Box>
                        <Box className="coord-label">{t("longitudeShort")}</Box>
                        <Box className="coord-value">{lon}</Box>
                    </Box>
                    <Box>
                        <Box className="coord-label">{t("latitudeShort")}</Box>
                        <Box className="coord-value">{lat}</Box>
                    </Box>
                    <Box>
                        <Box className="coord-label">{t("altitudeShort")}</Box>
                        <Box className="coord-value coord-value-z">{alt}</Box>
                    </Box>
                </>
            );
        } catch (ex) {
            console.warn(ex);
            telemetry.trackCaughtException({
                source: "locationHud.locationHud.formatCoordinate",
                category: "data",
                error: ex,
            });
            setShowXyz(true);
        }
    }

    if (!content) {
        const fmtOptions = { decimals: small ? 0 : 3 };
        const [x, y, z] = position
            ? [
                  convertAndFormat(uom, position[0], UnitType.length, fmtOptions),
                  convertAndFormat(uom, position[1], UnitType.length, fmtOptions),
                  convertAndFormat(uom, position[2], UnitType.length, fmtOptions),
              ]
            : ["-", "-", "-"];

        content = (
            <>
                <Box>
                    <Box className="coord-label">X</Box>
                    <Box className="coord-value">{x}</Box>
                </Box>
                <Box>
                    <Box className="coord-label">Y</Box>
                    <Box className="coord-value">{y}</Box>
                </Box>
                <Box>
                    <Box className="coord-label">Z</Box>
                    <Box className="coord-value coord-value-z">{z}</Box>
                </Box>
            </>
        );
    }

    return (
        <HudPanel
            sx={{ pointerEvents: "auto", cursor: tmZone ? "pointer" : undefined, position: "relative", flex: "auto" }}
            onClick={() => setShowXyz(!showXyz)}
        >
            <Tooltip title={t("coordinates")} placement="bottom">
                <Box
                    display="flex"
                    p={1}
                    sx={
                        small
                            ? {
                                  px: 1,
                                  py: 0,
                                  display: "flex",
                                  gap: 1,
                                  "& > div": {
                                      lineHeight: 1.25,
                                  },
                                  "& .coord-label": {
                                      color: theme.palette.text.secondary,
                                      fontSize: "0.75rem",
                                  },
                                  "& .coord-value": {
                                      fontSize: "0.75rem",
                                      paddingTop: 0.75,
                                      minWidth: "70px",
                                  },
                                  "& .coord-value.coord-value-z": {
                                      minWidth: "40px",
                                  },
                              }
                            : {
                                  p: 1,
                                  gap: 2,
                                  display: "flex",
                                  "& > div": {
                                      display: "flex",
                                      gap: 1,
                                  },
                                  "& .coord-value": {
                                      minWidth: showLatLon ? "100px" : "120px",
                                  },
                                  "& .coord-value.coord-value-z": {
                                      minWidth: "90px",
                                  },
                              }
                    }
                >
                    {content}
                </Box>
            </Tooltip>
            {small && <Scale small />}
        </HudPanel>
    );
}

function Scale({ small }: { small: boolean }) {
    const cameraState = useCameraState();
    const theme = useTheme();
    const isTopDown = useAppSelector(selectIsTopDown);
    const camera = useAppSelector(selectCamera);
    const { t } = useTranslation();
    const uom = useUom();

    const scale = useMemo(() => {
        if (!cameraState || !camera || camera.type !== CameraType.Orthographic) {
            return;
        }

        const fov = cameraState.fov;
        const height = window.innerHeight;
        const pxPerM = height / fov;
        const width = getNiceScaleMeters(pxPerM);
        return { width, px: Math.round(width * pxPerM) };
    }, [cameraState, camera]);

    if (!scale || !isTopDown) {
        return null;
    }

    if (small) {
        return (
            <Box
                sx={{
                    position: "absolute",
                    bottom: "0",
                    px: 2,
                    left: 0,
                    right: 0,
                    width: "auto",
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <Box
                    sx={{
                        pointerEvents: "auto",
                        px: 2,
                        py: 0,
                        width: scale.px,
                    }}
                >
                    <Tooltip title={t("scale")} placement="bottom">
                        <Box textAlign="center" width="100%" fontSize="0.7rem" color="grey">
                            {convertAndFormat(uom, scale.width, UnitType.length)}
                        </Box>
                    </Tooltip>
                    <Box position="relative">
                        <Box
                            width="100%"
                            height="4px"
                            position="absolute"
                            bottom={theme.spacing(0.25)}
                            sx={{ border: "1px solid black", borderTop: 0 }}
                        ></Box>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <HudPanel sx={{ pointerEvents: "auto", px: 2, width: scale.px, position: "relative" }}>
            <Tooltip title={t("scale")} placement="bottom">
                <Box textAlign="center" width="100%" p={1}>
                    {convertAndFormat(uom, scale.width, UnitType.length)}
                </Box>
            </Tooltip>
            <Box position="relative">
                <Box
                    width="100%"
                    height="8px"
                    position="absolute"
                    bottom={theme.spacing(1)}
                    sx={{ border: "1px solid black", borderTop: 0 }}
                ></Box>
            </Box>
        </HudPanel>
    );
}

function getNiceScaleMeters(pxPerM: number) {
    const desiredWidthPx = 150;
    const m = desiredWidthPx / pxPerM;
    let roundTo;
    if (m >= 100) {
        roundTo = 10;
    } else if (m >= 1) {
        roundTo = 1;
    } else if (m >= 0.1) {
        roundTo = 0.1;
    } else if (m >= 0.01) {
        roundTo = 0.01;
    } else if (m >= 0.001) {
        roundTo = 0.001;
    } else {
        return m;
    }
    return Math.round(m / roundTo) * roundTo;
}
